<template>
  <div class="page-wrapper">
    <!-- Tabs Wrapper -->
    <div class="tabs-wrapper">
      <div class="tabs-header">
        <b-tabs lazy>
          <b-tab :title="$t('globals.all_devices')" active></b-tab>
        </b-tabs>
        <div class="tabs-header-actions">
          <button
            class="action-btn import-btn"
            @click="$router.push({name: 'import'})"
          >
            {{ $t('globals.import').toUpperCase() }}
          </button>
          <button
            class="action-btn export-btn"
            @click="exportModalShow = true"
          >
            {{ $t('globals.export').toUpperCase() }}
          </button>
          <b-button
            variant="primary"
            class="create-btn"
            v-b-toggle.sidebar-1
          >
            {{ $t('globals.stats').toUpperCase() }}
          </b-button>
        </div>
      </div>
      <div class="tab-content">
        <div class="tab-content-inner">
          <!-- Search and Filters Section -->
          <div class="filters-section">
            <div class="search-container">
              <label class="search-label">IMEI</label>
              <b-form-input
                v-model="searchQuery.imei"
                @keyup.enter="searchDevicesLikeImei"
                class="search-input"
                placeholder="IMEI..."
                :disabled="getAllDevicesLoading"
              />
            </div>

            <div class="filter-group">
              <div class="filter-item">
                <label class="filter-label">{{ $t("globals.status") }}</label>
                <b-form-select
                  v-model="searchQuery.status"
                  :options="statusOptions"
                  class="filter-select"
                  :disabled="searchQuery.imei != null"
                />
              </div>

              <div class="filter-item">
                <label class="filter-label">{{ $t("globals.product") }}</label>
                <b-form-select
                  v-model="searchQuery.product"
                  :options="productOptions"
                  class="filter-select"
                  :disabled="searchQuery.imei != null"
                />
              </div>
            </div>

            <b-button
              variant="primary"
              class="search-btn"
              @click="selectFetch"
            >
              {{ $t("globals.search").toUpperCase() }}
            </b-button>
          </div>

          <!-- Table -->
          <div class="table-wrapper" style="position: relative; min-height: 300px;">
            <!-- Loading Overlay -->
            <div v-if="getAllDevicesLoading" class="loading-overlay">
              <div class="spinner"></div>
              <p>{{ $t("globals.loading") }}...</p>
            </div>

            <!-- Table Content -->
            <table class="modern-table">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    :key="field.key"
                    :class="{ sortable: field.sortable }"
                    @click="field.sortable && handleSort(field.key)"
                  >
                    {{ field.label }}
                    <span v-if="sortBy === field.key" class="sort-icon">
                      {{ sortDesc ? '▼' : '▲' }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- Empty State Row -->
                <tr v-if="!getAllDevicesLoading && getAllDevices.length === 0">
                  <td :colspan="fields.length" class="empty-state-cell">
                    {{ $t("globals.no_results") }}
                  </td>
                </tr>

                <!-- Data Rows -->
                <tr
                  v-for="item in getAllDevices"
                  :key="item.id"
                  class="table-row"
                  @click="rowClicked(item)"
                >
                  <td>{{ item.id }}</td>
                  <td>{{ item.imei }}</td>
                  <td>{{ item.product }}</td>
                  <td>
                    <span class="status-badge" :class="`status-${item.status}`">
                      {{ $t('globals.' + item.status) }}
                    </span>
                  </td>
                  <td>
                    <router-link
                      v-if="item.customer"
                      class="customer-link"
                      :to="{name: 'customer', params:{ id: item.customer.id }}"
                      @click.native.stop
                      target="_blank"
                    >
                      {{ item.customer.company_name }}
                    </router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <router-link
                      v-if="item.order"
                      class="customer-link"
                      :to="{ name: 'order', params:{ id: item.order.id } }"
                      @click.native.stop
                      target="_blank"
                    >
                      {{ item.order.order_no }}
                    </router-link>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <ulu-logo v-if="item.brand" :brand="item.brand" scale="1.2" v-b-tooltip.hover :title="item.brand" />
                    <span v-else>-</span>
                  </td>
                  <td>{{ item.batch || '-' }}</td>
                  <td v-b-tooltip.hover :title="item.created_at">
                    {{ item.created_at ? item.created_at.substring(0, 10) : '-' }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="pagination-section">
            <div class="controls-left">
              <span class="summary-label">{{ $t("globals.showing") }} {{ offset + 1 }}-{{ Math.min(offset + limit, offset + getAllDevices.length) }}</span>
            </div>
            <div class="pagination-controls">
              <button
                class="page-btn"
                :disabled="offset === 0 || getAllDevicesLoading"
                @click="fetchLess"
              >
                &lt;
              </button>
              <button
                class="page-btn"
                :disabled="getAllDevices.length < limit || getAllDevicesLoading"
                @click="fetchMore"
              >
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sidebar -->
    <b-sidebar width="450px" id="sidebar-1" shadow lazy>
      <ulu-all-devices-stats-sidebar />
    </b-sidebar>

    <!-- Modals -->
    <export-devices-modal :show="exportModalShow" :close="closeModals"/>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    "ulu-all-devices-stats-sidebar": () =>
      import("@/components/devices/all_devices/AllDevicesStatsSidebar.vue"),
    "export-devices-modal": () =>
      import("@/components/devices/all_devices/ExportDevicesModal.vue"),
    "ulu-logo": () =>
      import("@/components/common/Logo.vue")
  },
  data() {
    return {
      exportModalShow: false,
      searchQuery: {
        imei: null,
        product: null,
        status: null,
      },
      statusOptions: [
        { text: this.$t('globals.all'), value: null },
        { text: this.$t('globals.active'), value: 'active' },
        { text: this.$t('globals.stock'), value: 'stock' },
        { text: this.$t('globals.terminated'), value: 'terminated' },
        { text: this.$t('globals.warranty'), value: 'warranty' },
      ],
      productOptions: [
        { text: this.$t('globals.all'), value: null },
        { text: 'Dongel', value: 'dongel' },
        { text: 'Blackbox', value: 'blackbox' },
        { text: 'Blackbox CT', value: 'blackbox_ct' },
        { text: 'Blackbox 4g', value: 'blackbox4g' },
        { text: 'Dongel 4g', value: 'dongel4g' },
        { text: 'Cmm1', value: 'cmm1' },
        { text: 'Cmm3', value: 'cmm3' },
        { text: 'Cmm4', value: 'cmm4' },
        { text: 'At4m', value: 'at4m' },
        { text: 'TwDashcam', value: 'twdashcam' },
        { text: 'Velcare', value: 'velcare' },
        { text: 'Velcare VB', value: 'velcare_vb' },
        { text: 'CMM2HC SS', value: 'cmm2hc_ss' },
      ],
      offset: 0,
      limit: 20,
      sortBy: "created_at",
      sortDesc: true,
      fields: [
        {
          key: "id",
          label: "ID",
          sortable: true
        },
        {
          key: "imei",
          label: "IMEI",
          sortable: true
        },
        {
          key: "product",
          label: this.$t("globals.product"),
          sortable: true
        },
        {
          key: "status",
          label: this.$t("globals.status"),
          sortable: true
        },
        {
          key: "customer",
          label: this.$t("globals.customer"),
          sortable: true
        },
        {
          key: "order",
          label: this.$t("globals.order_no"),
          sortable: true
        },
        {
          key: "brand",
          label: this.$t("globals.brand"),
          sortable: true
        },
        {
          key: "batch",
          label: this.$t("globals.batch"),
          sortable: true
        },
        {
          key: "created_at",
          label: this.$t("globals.created_at"),
          sortable: true
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["getUserBrandPermissions"]),
    ...mapGetters("devices", ["getAllDevices", "getAllDevicesLoading"]),
  },
  methods: {
    ...mapActions("devices", [
      "fetchDevicesPagination",
      "fetchDevicesLikeImei",
    ]),
    rowClicked(item) {
      // Don't navigate if user is selecting text
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      this.$router.push({ name: "device", params: { id: item.id } });
    },
    handleSort(fieldKey) {
      if (this.sortBy === fieldKey) {
        this.sortDesc = !this.sortDesc;
      } else {
        this.sortBy = fieldKey;
        this.sortDesc = false;
      }
      this.fetch();
    },
    fetchMore() {
      this.offset += 20;
      this.selectFetch();
    },
    fetchLess() {
      if (this.offset >= 20) {
        this.offset -= 20;
        this.selectFetch();
      }
    },
    resetOffsetSort() {
      this.offset = 0;
      this.sortBy = "created_at";
      this.sortDesc = true;
    },
    resetQuery() {
      this.searchQuery.imei = null;
      this.searchQuery.product = null;
      this.searchQuery.status = null;
    },
    fetch() {
      let order_by = this.sortBy;
      if (this.sortDesc) {
        order_by += " DESC";
      } else {
        order_by += " ASC";
      }

      this.fetchDevicesPagination({
        this_ref: this,
        status: this.searchQuery.status,
        product: this.searchQuery.product,
        limit: this.limit,
        offset: this.offset,
        order: order_by
      });
    },
    selectFetch() {
      this.fetch();
    },
    resetReload() {
      this.resetOffsetSort();
      this.resetQuery();
      this.fetch();
    },
    searchDevicesLikeImei() {
      if (this.searchQuery.imei != null && this.searchQuery.imei.length > 2) {
        this.searchQuery.product = null;
        this.searchQuery.status = null;

        this.fetchDevicesLikeImei({
          imei: this.searchQuery.imei,
          this_ref: this,
          limit: this.limit,
          offset: this.offset,
        });
      }
    },
    closeModals() {
      this.exportModalShow = false;
      this.resetReload();
    },
  },
  created() {
    if (!this.getUserBrandPermissions.devices_index) {
      this.$router.push({ name: "dashboard" });
    }
  },
  watch: {
    'searchQuery.status'(newVal) {
      this.resetOffsetSort();
      this.selectFetch();
    },
    'searchQuery.product'(newVal) {
      this.resetOffsetSort();
      this.selectFetch();
    },
    'searchQuery.imei'(newVal) {
      if (newVal) {
        this.resetOffsetSort();
      }
    }
  },
  mounted() {
    this.resetReload();
  }
};
</script>
